import { A as AbortError } from './abort-error.js';
import { H as HtmlMediaSubtitles, A as AdapterBaseHtmlMedia } from './adapter-base-htmlmedia.js';
import { H as HlsMediaInfo } from './hls-media-info.js';
import { destroyableEventListener, DestroyableBase, DestroyableAbortController, whenDestroyed, DestroyableSet } from '@nrk/destroyable';
import { g as getLogger } from './logger.js';
import { a as PlaybackSessionError } from './playback-session-error.js';
import { c as canPlayType } from './can-play-type3.js';
import { e as convertTimeRanges, i as implementsStatic } from './time-range.js';
import { Observable } from 'zen-observable-ts';
import { i as isAbortError, t as toError } from './error.js';
import { g as guessMediaTypeOfUrl, i as isHlsMediaType, a as isAudioOnlyMediaType } from './media-type.js';

const logger$1 = getLogger('HlsNativeSubtitles');
/**
 * The HLS native subtitles differ from standard subtitles in two ways:
 *
 * Text tracks are matched only by label (NAME in m3u8), not language. The HLS
 * specification requires the name to be present, but the language is optional,
 * although HTML5 requires the language but not the label. The name is required
 * be to unique within a media group (multiple media groups with subtitles
 * isn't really supported). Safari (iPad, at least) seems to translate
 * macro-language codes to individual language codes (no -> nb) when creating
 * text tracks, so the language can't be trusted to match the m3u8 data.
 *
 * Safari doesn't create the text tracks immediately, so until the tracks
 * appear on the media element, they are faked in order to make it possible to
 * select subtitles (e.g. enable a default) before playback.
 *
 * Adding subtitles directly to the media element, not via the session API,
 * would quickly break things.
 *
 * EXT-X-MEDIA in the HLS specification:
 * https://tools.ietf.org/html/draft-pantos-http-live-streaming-23#section-4.3.4.1
 */

class HlsNativeSubtitles extends HtmlMediaSubtitles {
  constructor(element, eventEmitter, configs) {
    super(element, eventEmitter, configs); // Check the state of the text track list.

    this.placeholderTextTrack = void 0;
    this.numMissingTracks = void 0;
    this.trackListenerHandle = void 0;
    logger$1.log(`${element.textTracks.length} of ${configs.length} text tracks are present.`);
    this.numMissingTracks = configs.length - element.textTracks.length;

    if (this.numMissingTracks > 0) {
      this.trackListenerHandle = destroyableEventListener(element.textTracks, 'addtrack', this.handleTrackAdded.bind(this));
    }
  }

  supportsAddingTracks() {
    return false;
  }

  addSubtitlesTrack() {
    throw new Error('Not supported.');
  }

  getTextTrack(config) {
    if (!this.element) {
      return undefined;
    }

    const textTrackList = this.element.textTracks; // Only match the subtitles by label.

    for (let i = 0; i < textTrackList.length; ++i) {
      const track = textTrackList[i];

      if (track.kind === 'subtitles' && track.label === config.label) {
        return track;
      }
    } // If there are missing tracks, then assume this was one of them and create
    // a fake track object.


    if (this.numMissingTracks > 0) {
      logger$1.log(`Creating a placeholder text track (${config.label}, ${config.language}) in anticipation of the real track.`); // Whichever properties are needed to make it work.

      this.placeholderTextTrack = {
        label: config.label,
        language: config.language,
        mode: 'disabled',
        oncuechange: null
      };
      return this.placeholderTextTrack;
    }

    return undefined;
  }

  handleTrackAdded(event) {
    const track = event.track;
    const fakeTrack = this.placeholderTextTrack;

    if (!track || !(track instanceof TextTrack) || track.kind !== 'subtitles') {
      return;
    } // Assume that the added text track was for one of the subtitle configs.


    this.numMissingTracks--;

    if (this.numMissingTracks <= 0 && this.element) {
      var _this$trackListenerHa;

      (_this$trackListenerHa = this.trackListenerHandle) == null ? void 0 : _this$trackListenerHa.destroy();
      this.trackListenerHandle = undefined;
      logger$1.log('All text tracks are now present.');
    }

    if (fakeTrack && fakeTrack.label === track.label) {
      logger$1.log(`Placeholder track (${fakeTrack.label}, ${fakeTrack.language}) replaced by real track${fakeTrack.language !== track.language ? ` (language changed to ${track.language}!)` : ''}.`);
      track.mode = fakeTrack.mode;
      track.oncuechange = fakeTrack.oncuechange; // Replace the active track (should always be the case at this point,
      // really), due to how getTextTrack() is used by the super class.

      if (this.activeTrack === fakeTrack) {
        this.activeTrack = track;
      }

      this.placeholderTextTrack = undefined;
    }
  }

  destroy() {
    var _this$trackListenerHa2;

    (_this$trackListenerHa2 = this.trackListenerHandle) == null ? void 0 : _this$trackListenerHa2.destroy();
    this.trackListenerHandle = undefined;
    super.destroy();
  }

}

/**
 * Listen to an array of HTML events through an observable.
 */

function eventTargetObservable(target, events) {
  return new Observable(observer => {
    const proxy = event => observer.next(event);

    events.forEach(event => target.addEventListener(event, proxy));
    return () => events.forEach(event => target.removeEventListener(event, proxy));
  });
}

/**
 * Observe the seekable time range of an HTMLMediaElement. The start, end, if
 * it's final and when.
 */

function htmlMediaRangeObservable(mediaElement) {
  return new Observable(observer => {
    let previousStart;
    let previousEnd;

    const check = () => {
      const timestamp = Date.now() / 1000;
      const duration = mediaElement.duration;

      if (Number.isNaN(duration)) {
        return;
      }

      let start;
      let end;

      if (mediaElement.seekable !== undefined && mediaElement.seekable.length > 0) {
        start = mediaElement.seekable.start(0);
        end = mediaElement.seekable.end(0);
      } else {
        start = 0;
        end = duration;
      }

      if (Number.isFinite(duration)) {
        observer.next({
          start,
          end,
          complete: true,
          timestamp
        });
        observer.complete();
      } else {
        if (start !== previousStart || end !== previousEnd) {
          previousStart = start;
          previousEnd = end;
          observer.next({
            start,
            end,
            complete: false,
            timestamp
          });
        }
      }
    };

    check();

    if (!observer.closed) {
      const eventSub = eventTargetObservable(mediaElement, ['durationchange', 'loadeddata', 'loadedmetadata', 'progress']).subscribe(() => check());
      return () => eventSub.unsubscribe();
    }
  });
}

/**
 * Observe the video quality (width, height and potentially frame rate) of an
 * HTMLVideoElement.
 *
 * Detection happens by polling the relevant values. The poll frequency
 * defaults to 500 ms, but can be specified in the options. Polling only
 * happens when the player is playing.
 *
 * Frame rate detection is only enabled if the media's unique frame rates are
 * provided in the frameRate` option. The array must only contain unique
 * values, but doesn't have to be ordered.
 */
function htmlVideoQualityObservable(videoElement, options) {
  const logger = getLogger('VideoQualityObservable');
  return new Observable(observer => {
    var _options$frameRates;

    let previousWidth;
    let previousHeight;
    let previousFrameRate;
    let previousFrameCount = 0;
    let previousFrameCountTime = 0;
    let dropNextFrameCount = true;
    const frameRates = (_options$frameRates = options == null ? void 0 : options.frameRates) != null ? _options$frameRates : [];
    frameRates.sort(); // Snap distance is the shortest half-distance between any two frame rates.
    // It's best if 25 and 30 or 50 and 60 won't be in the same list of frame
    // rates, which is unlikely anyway.

    const snapDistance = Math.floor(frameRates.reduce((diff, frameRate, index) => index === 0 ? frameRate : Math.min(diff, (frameRates[index] - frameRates[index - 1]) / 2), 0));
    const frameRateDetectionEnabled = !!videoElement.getVideoPlaybackQuality && frameRates.length > 1;

    if (frameRateDetectionEnabled) {
      logger.log(`Frame rate detection enabled. Got: ${frameRates.join(', ')}. Snap distance: ${snapDistance}.`);
    } else {
      logger.log(`Frame rate detection disabled. ${frameRates.length > 1 ? 'No support.' : 'Single frame rate.'}`);
    }

    const check = () => {
      let qualityChanged = false;
      const width = videoElement.videoWidth;
      const height = videoElement.videoHeight;

      if (width !== previousWidth || height !== previousHeight) {
        logger.log(`Resolution changed from ${previousWidth}x${previousHeight} to ${width}x${height}.`);
        previousWidth = width;
        previousHeight = height;
        qualityChanged = true; // In case the frame rate is changing too, drop this frame count to not
        // mix the frame rates before and after the quality change, as it won't
        // match any of them.

        dropNextFrameCount = true;
      }

      if (frameRateDetectionEnabled) {
        const frameCounts = videoElement.getVideoPlaybackQuality();
        const frameCount = frameCounts.totalVideoFrames;
        const frameCountTime = frameCounts.creationTime;
        const frameRate = Math.round((frameCount - previousFrameCount) / (frameCountTime - previousFrameCountTime) * 1000);
        previousFrameCount = frameCount;
        previousFrameCountTime = frameCountTime;

        if (frameRate === 0) {
          // The next frame count will be broken.
          dropNextFrameCount = true;
        } else if (dropNextFrameCount) {
          // One frame count dropped.
          dropNextFrameCount = false;
        } else {
          // Snap to an available frame rate and use that.
          for (let i = 0; i < frameRates.length; ++i) {
            if (Math.abs(frameRate - frameRates[i]) <= snapDistance) {
              if (frameRates[i] !== previousFrameRate) {
                qualityChanged = true;
                logger.log(`Frame rate changed from ${previousFrameRate} to ${frameRates[i]}`);
                previousFrameRate = frameRates[i];
              }

              break;
            }
          }
        }
      }

      if (qualityChanged) {
        observer.next({
          width: previousWidth,
          height: previousHeight,
          frameRate: previousFrameRate
        });
      }
    }; // Run control.


    let intervalId;

    const start = () => {
      if (intervalId === undefined) {
        var _options$pollFrequenc;

        check();
        intervalId = setInterval(check, (_options$pollFrequenc = options == null ? void 0 : options.pollFrequency) != null ? _options$pollFrequenc : 500);
        logger.log('Tracking video quality change.');
      }
    };

    const stop = () => {
      if (intervalId !== undefined) {
        clearInterval(intervalId);
        intervalId = undefined;
        logger.log('Stopped tracking video quality change.');
      }
    };

    const events = ['loadeddata', 'play', 'playing', 'paused', 'ended'];
    const eventSub = eventTargetObservable(videoElement, events).subscribe(event => {
      if (event.type === 'paused' || event.type === 'ended') {
        stop();
      } else {
        start();
      }
    });

    if (!videoElement.paused) {
      start();
    }

    return () => {
      eventSub.unsubscribe();
      stop();
    };
  });
}

/**
 * Destroyable handle for Observables (zen-observable-ts).
 */
class DestroyableSubscription extends DestroyableBase {
  constructor(observable, ...args) {
    super();
    this.subscription = void 0;
    const [onNext, onError, onComplete] = args;
    this.subscription = observable.subscribe(onNext, onError, () => {
      onComplete == null ? void 0 : onComplete();
      this.destroy();
    });
  }

  destroy() {
    this.subscription.unsubscribe();
    super.destroy();
  }

}
/**
 * Subscribe to an observable and return a destroyable handle.
 */

const subscribe = (observable, ...args) => new DestroyableSubscription(observable, ...args);

var _dec, _class;
const adapterInfo = {
  id: 'Native',
  name: 'Native adapter',
  tech: 'HTML5',
  version: 'n/a'
};
const logger = getLogger('NativeAdapter');
let NativeAdapter = (_dec = implementsStatic(), _dec(_class = class NativeAdapter extends AdapterBaseHtmlMedia {
  constructor(context) {
    var _context$media$mediaT;

    super(context);
    this.qualityTracker = void 0;
    this.mediaInfo = void 0;
    this.startTime = void 0;
    this.startTimeLive = void 0;
    this.mediaRangeObservable = void 0;
    const mediaType = (_context$media$mediaT = context.media.mediaType) != null ? _context$media$mediaT : guessMediaTypeOfUrl(context.media.url);

    if (mediaType === undefined) {
      throw new Error('Failed to resolve media type. Please specify.');
    }

    if (isHlsMediaType(mediaType)) {
      this.mediaInfo = new HlsMediaInfo(context.media);
    } else {
      this.mediaInfo = { ...context.media,
        type: 'basic'
      };
      this.mediaInfo.isAudioOnly = isAudioOnlyMediaType(mediaType);
    }

    this.qualityTracker = context.qualityTracker;
    this.applyInitialPlaybackState(context.initialPlaybackState);
    this.mediaRangeObservable = htmlMediaRangeObservable(context.mediaElement);
    this.addDestroyable(subscribe(this.mediaRangeObservable, mediaRange => this.updateMediaRange(mediaRange)));
  }

  static canPlayType(mediaType) {
    return canPlayType(mediaType);
  }

  static getAdapterInfo() {
    return adapterInfo;
  }

  getAdapterInfo() {
    return adapterInfo;
  }

  getMediaInfo() {
    return this.mediaInfo;
  }

  applyInitialPlaybackState(state) {
    const {
      playbackRate,
      mute,
      volume,
      startTime,
      startTimeLive
    } = state != null ? state : {};

    if (playbackRate !== undefined) {
      this.setPlaybackRate(playbackRate);
    }

    if (mute === true) {
      this.mute();
    } else if (mute === false) {
      this.unmute();
    }

    if (volume !== undefined) {
      this.setVolume(volume);
    }

    this.startTime = startTime;
    this.startTimeLive = startTimeLive;
  }

  async internalPreload(preloadDestroyables, options) {
    // Not all browsers support preload = 'metadata'. Media not based on
    // segments are typically loaded completely (loadeddata) unless preload =
    // 'none'.
    const htmlMediaElementPreloadValue = this.startTimeLive !== undefined ? 'auto' : (options == null ? void 0 : options.loadData) !== undefined ? options.loadData ? 'auto' : 'metadata' : undefined;
    logger.debug(`Preload attribute: ${htmlMediaElementPreloadValue}`);

    if (htmlMediaElementPreloadValue !== undefined) {
      this.mediaElement.preload = htmlMediaElementPreloadValue;
    }

    const abortController = new DestroyableAbortController();
    preloadDestroyables.addDestroyable(abortController);
    let firstMediaRange;
    let loader;
    let loaderAborted = false; // Async: Pick up the first media range/isLive state when available.
    // TODO: This doesn't work with preload=none.

    const firstMediaRangeObserver = subscribe(this.mediaRangeObservable, mediaRange => {
      logger.log('First media range:', mediaRange);
      firstMediaRange = mediaRange;
      firstMediaRangeObserver.destroy();
    });
    preloadDestroyables.addDestroyable(firstMediaRangeObserver); // Set up promises for loading media info etc.

    if (this.mediaInfo.type === 'hls') {
      const mediaInfo = this.mediaInfo; // Type HlsMediaInfo.

      loader = mediaInfo.loadMasterPlaylist({
        signal: abortController.signal
      }).then(() => {
        var _mediaInfo$masterPlay;

        this.createSubtitleControls((_mediaInfo$masterPlay = mediaInfo.masterPlaylist) == null ? void 0 : _mediaInfo$masterPlay.subtitles);
        this.configureQualityTracker(mediaInfo.qualities, mediaInfo.isAudioOnly);
        logger.log('Master playlist loaded.');
      }).then(() => whenDestroyed(firstMediaRangeObserver)) // This doesn't throw if aborted.
      .then(() => {
        if (firstMediaRange === undefined) {
          loaderAborted = true;
          throw new AbortError('Media range observation was aborted.');
        }

        if (firstMediaRange.complete) {
          logger.log('On-demand HLS stream.');
          return this.applyStartTime();
        } else {
          logger.log('Live HLS stream.');
          const mediaRange = { ...firstMediaRange
          }; // Not undefined.
          // Need program date/time and more from the HLS media playlist.

          return mediaInfo.loadMediaPlaylist({
            signal: abortController.signal
          }).then(() => this.setHlsProgramDateTime(mediaRange, mediaInfo)).then(() => this.applyStartTime());
        }
      }).catch(e => {
        if (isAbortError(e)) {
          loaderAborted = true;
          return;
        }

        const error = PlaybackSessionError.fromUnknownError(e, this.playbackStarted, 'Failed to load HLS playlists.');
        this.handleError(error);
      });
    } else {
      loader = whenDestroyed(firstMediaRangeObserver).then(() => {
        if (firstMediaRange === undefined) {
          loaderAborted = true;
        } else {
          return this.applyStartTime();
        }
      });
    } // Make the ball start rolling.


    logger.debug(`Setting src to: ${this.mediaInfo.url}`);
    this.mediaElement.src = this.mediaInfo.url; // Wait for the promise chain above to complete.

    await loader;

    if (loaderAborted) {
      throw new AbortError('Preload was aborted.');
    }
  }

  setHlsProgramDateTime(mediaRange, mediaInfo) {
    var _mediaInfo$programDat;

    const programDateTime = (_mediaInfo$programDat = mediaInfo.programDateTime) != null ? _mediaInfo$programDat : this.guessHlsProgramDateTime(mediaInfo.playlistDuration, mediaInfo.averageSegmentDuration); // Patch the media range, if necessary. Safari subtracts about 3 segments
    // from video.seekable, so be careful and do the same when patching the
    // first media range. Safari will provide its own value as soon as the
    // stream is properly loaded. Don't set a live position based on the
    // artificial duration. Wait for Safari.

    if (!Number.isFinite(mediaRange.end)) {
      const artificialDuration = Math.max(0, mediaInfo.playlistDuration - 3 * mediaInfo.averageSegmentDuration);
      logger.log('Patching media range.', {
        seekableEnd: mediaRange.end,
        playlistDuration: mediaInfo.playlistDuration,
        usingRangeEnd: artificialDuration
      });
      mediaRange.end = artificialDuration;
    }

    this.handleMediaRangeUpdate(mediaRange, undefined, programDateTime);
  }
  /**
   * Send a fresh media range or program date/time to the time super class.
   * Normalize a little and resolve the live position for live streams (it's
   * ignored for on-demand media).
   */


  updateMediaRange(range, programDateTime) {
    range.start = Math.max(0, range.start);
    range.end = Math.max(range.start, range.end);
    const averageSegmentDuration = this.mediaInfo.type === 'hls' ? this.mediaInfo.averageSegmentDuration : 10; // Subject for discussion and change: Safari has already subtracted 3
    // segments from the total duration, but here we subtract another 3 to make
    // the scrubber slide nicely. Safari starts playback at the seekable end
    // and lets the playhead run beyond that, and then drip feed us the
    // seekable segments basically after they have been already played, which
    // means that seeking to the live position would go backwards. But we want
    // to have a sliding scrubber where it's always possible to seek to the end
    // of the scrubber range, within the seekable range. So then we need to
    // subtract more to be able to smooth over the "descrete" availability of
    // more segments. We don't change the starting point of the playhead, but
    // it's not possible to get back to that position after a bit of pausing or
    // seeking into the buffer. Also, AdapterBaseTime makes sure to not seek
    // backwards to the live position if the playhead is ahead.

    const livePosition = Number.isFinite(range.end) ? Math.max(0, range.end - 3 * averageSegmentDuration) : undefined;
    this.handleMediaRangeUpdate(range, livePosition, programDateTime);
  }

  createSubtitleControls(subtitles) {
    if (this.subtitleControls !== undefined || !(subtitles != null && subtitles.length)) {
      return;
    }

    logger.log('Creating controls for media provided subtitles:', subtitles.map(s => s.label).join(', '));
    this.subtitleControls = new HlsNativeSubtitles(this.mediaElement, this.eventEmitter, subtitles);
    this.subtitleControls.setDefaultSubtitles();
  }
  /**
   * Quality tracking applies to videos played on a video element when
   * qualities are known (variable bitrate streams). Quality tracking is based
   * on polling the video dimensions, as HTML5 doesn't provide other means, so
   * audio quality can't be tracked.
   */


  configureQualityTracker(qualities, isAudioOnly) {
    if (qualities === undefined || isAudioOnly || !(this.mediaElement instanceof HTMLVideoElement)) {
      return;
    }

    logger.log('Setting up quality tracking (polling) for video.');
    this.qualityTracker.setAvailableQualities(qualities);

    const isNotUndefined = value => value !== undefined;

    const frameRates = Array.from(new Set(qualities.map(q => q.frameRate).filter(isNotUndefined)));
    this.addDestroyable(subscribe(htmlVideoQualityObservable(this.mediaElement, {
      frameRates
    }), quality => this.qualityTracker.setCurrentQualityByVideoProps(quality.width, quality.height, quality.frameRate)));
  }
  /**
   * Apply start time. The method will return before the seek is applied or the
   * seek is completed if it determines that a call to play() is required for
   * further progress, in order to not create a deadlock. The seek and seeked
   * logic will still be applied even if the method returns early, just not
   * waited for.
   */


  async applyStartTime() {
    var _mediaInfo$mediaPlayl,
        _mediaInfo$mediaPlayl2,
        _mediaInfo$mediaPlayl3,
        _this$startTimeLive,
        _this$startTime,
        _this = this;

    const mediaElement = this.mediaElement;
    const mediaInfo = this.mediaInfo;
    const startTimeLiveApplies = mediaInfo.type === 'hls' && ((_mediaInfo$mediaPlayl = mediaInfo.mediaPlaylist) == null ? void 0 : _mediaInfo$mediaPlayl.fixedEnd) === false;
    const startTimeApplies = mediaInfo.type !== 'hls' || ((_mediaInfo$mediaPlayl2 = mediaInfo.mediaPlaylist) == null ? void 0 : _mediaInfo$mediaPlayl2.fixedStart) !== false;
    const holdWhileBuffering = mediaInfo.type === 'hls' && ((_mediaInfo$mediaPlayl3 = mediaInfo.mediaPlaylist) == null ? void 0 : _mediaInfo$mediaPlayl3.fixedEnd) === false;
    logger.debug('Start time data:', {
      startTimeLiveApplies,
      startTimeApplies,
      startTimeLive: (_this$startTimeLive = this.startTimeLive) != null ? _this$startTimeLive : 'n/a',
      startTime: (_this$startTime = this.startTime) != null ? _this$startTime : 'n/a',
      holdWhileBuffering
    });
    let startTime;

    if (startTimeLiveApplies && this.startTimeLive !== undefined) {
      startTime = this.liveTimeToPosition(this.startTimeLive);
    } else if (startTimeApplies && this.startTime !== undefined) {
      startTime = this.startTime;
    } else {
      return;
    } // Hack! Safari doesn't manage to seek to 0 on event streams for some
    // reason. Maybe it's the test stream. See the event-stream-start-time.ts
    // browser test.


    if (startTimeLiveApplies && startTimeApplies && startTime === 0) {
      startTime = 0.01;
    }

    const waitForLoadedMetadata = async function waitForLoadedMetadata() {
      const destroyable = destroyableEventListener(mediaElement, 'loadedmetadata', () => {
        destroyable.destroy();
      });

      _this.addDestroyable(destroyable);

      await whenDestroyed(destroyable);

      if (mediaElement.readyState < mediaElement.HAVE_METADATA) {
        throw new AbortError('Aborted.');
      }

      logger.log('Got loadedmetadata');
    };

    const waitForLoadedData = async function waitForLoadedData() {
      const destroyable = destroyableEventListener(mediaElement, 'loadeddata', () => {
        destroyable.destroy();
      });

      _this.addDestroyable(destroyable);

      await whenDestroyed(destroyable);

      if (mediaElement.readyState < mediaElement.HAVE_CURRENT_DATA) {
        throw new AbortError('Aborted.');
      }

      logger.log('Got loadeddata');
    }; // Wait until a) the seek is done or b) call to play() is required. Play
    // won't be called until this destroyable is destroyed, so don't create a
    // deadlock. The promise chain below will attempt to complete the seek to
    // start time regardless of when doneOrRequirePlay is destroyed.


    const doneOrRequirePlay = new DestroyableSet();

    const checkIfRequirePlay = async function checkIfRequirePlay() {
      if (!doneOrRequirePlay.destroyed && mediaElement.networkState !== mediaElement.NETWORK_LOADING) {
        logger.log('Media not loading. Play required to continue start time seeking.');
        doneOrRequirePlay.destroy();
        return waitForLoadedData();
      }
    };

    const seek = async function seek() {
      const seeking = new DestroyableSet(_this); // Seek and keep seeking until the seeking event arrives.

      seeking.addDestroyables([destroyableEventListener(mediaElement, 'seeking', () => seeking.destroy()), destroyableEventListener(mediaElement, 'progress', () => {
        logger.log(`Calling seekTo: ${startTime}`);

        _this.seekTo(startTime);
      })]);
      logger.log(`Calling seekTo: ${startTime}`);

      _this.seekTo(startTime);

      await whenDestroyed(seeking);
    };

    if (holdWhileBuffering) {
      // Require sufficient buffering for live streams: Safari 9 jumps to the
      // end of the live stream if calling play just after seeked. But if at
      // least 3 segments are buffered from the seek target, then playback
      // starts at the seek target.
      const minBuffer = 3 * mediaInfo.averageSegmentDuration;
      let previousBufferAmount = -1;
      let repeatBufferEvents = 0;
      doneOrRequirePlay.addDestroyable(destroyableEventListener(mediaElement, 'progress', () => {
        const buffer = convertTimeRanges(mediaElement.buffered); // Find the buffer range around the start time. The buffer range and
        // seek targets aren't completely accurate. Maybe they skip to the
        // nearest frame. Allow some inaccuracy around the buffer
        // start/startTime (the "-1").

        const bufferRange = buffer.find(range => range.start - 1 <= startTime && range.end >= startTime);
        const bufferLength = bufferRange !== undefined ? bufferRange.end - bufferRange.start : 0;
        logger.log(`Buffered media at startTime: ${bufferLength}s`); // Safari 15 seems to only buffer 1 segment. So let it continue when
        // the buffered range is unchanged for a few consecutive progress
        // events.

        if (bufferLength < minBuffer) {
          if (bufferLength === previousBufferAmount) {
            if (++repeatBufferEvents === 2) {
              logger.log('Got 3 progress events with equal buffer length. Letting it continue...');
              doneOrRequirePlay.destroy();
            }
          } else {
            previousBufferAmount = bufferLength;
            repeatBufferEvents = 0;
          }

          return;
        }

        logger.log('Seek target sufficiently buffered');
        doneOrRequirePlay.destroy();
      }));
    } else {
      doneOrRequirePlay.addDestroyables([destroyableEventListener(mediaElement, 'seeked', () => {
        logger.log('Seeked to start time:', mediaElement.currentTime);
        doneOrRequirePlay.destroy();
      }), destroyableEventListener(mediaElement, 'progress', () => {
        logger.log('Progress:', {
          currentTime: mediaElement.currentTime,
          duration: mediaElement.duration,
          readyState: mediaElement.readyState,
          networkState: mediaElement.networkState
        });

        if (mediaElement.readyState >= mediaElement.HAVE_FUTURE_DATA) {
          // Fail fast instead of waiting forever/timeout.
          error = new Error('Got future or enough data but no seeked event. Browser is broken.');
          doneOrRequirePlay.destroy();
        }
      })]);
    }

    let error;
    Promise.resolve().then(() => checkIfRequirePlay()).then(() => mediaElement.readyState < mediaElement.HAVE_METADATA ? waitForLoadedMetadata().then(() => checkIfRequirePlay()) : undefined).then(() => seek()).then(() => checkIfRequirePlay()).catch(e => {
      if (doneOrRequirePlay.destroyed) {
        logger.error('Seeking to start time failed:', e);
      } else {
        error = toError(e);
        doneOrRequirePlay.destroy();
      }
    });
    await whenDestroyed(doneOrRequirePlay);

    if (error !== undefined) {
      throw error;
    }
  }

}) || _class);

export { NativeAdapter as N };
